/**
 * The Finance BOARD: Finance's own work in eMerge, one row per handoff to
 * Finance (a UMED obligation, or a private-pay sale a Patient Advocate handed
 * straight to Finance, 20261008b), built on the board kit (components/board;
 * docs/design/boards.md).
 *
 * Authority ruling (Isaiah Cotto, 2026-10-05): eMerge is the canonical
 * operational system; Monday is migration and provenance only. The tabs are
 * eleven eMerge handoff states, from one server-side expression, that add up
 * to Main table; the default view is Open work (the first nine). Nothing here
 * derives a status from time: there is no Past Due and no ageing.
 *
 * Monday Finance rows that eMerge has not reconciled yet sit in a TEMPORARY
 * RECONCILIATION LENS, "Legacy Monday rows not yet reconciled", outside Main
 * table. Monday's status word and money appear only there, in columns under
 * "Monday (legacy reference, read-only)"; a row leaves the lens once eMerge
 * holds the handoff. Opening a row docks the resident's Finance dossier
 * beside the table. Approval, NOCA and FLMMIS appear when the server sends
 * them (every Finance caller); "Approved" is the UMED operator's act
 * (2026-10-06), never FLMMIS reflected on its own. A correction after the
 * handoff is an amendment on it, marked "Corrected <date>" (ruling #1,
 * 2026-10-07), never a second row. No SSN.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BoardTabs, BoardTable, BoardLegend, ColumnKey, Pager, StatusBar, StateMark, NotRecorded, SourceTag,
  useBoardUrlState, boardDate, boardStamp, boardMoney, tabSourceText, panelId, tabId,
} from '../../components/board';
import type { BoardColumn, BoardTabDef } from '../../components/board';
import { StateBlock } from '../../components/tide';
import { outcomeOf, FinanceScope, awaitingCorrectionLine, AWAITING_CORRECTION_WHY, correctedWord, scheduleLine, payerLine } from './financeApi';
import {
  financeBoardApi, FinanceBoardPage, FinanceBoardRow, FinanceFilterOption, MondayMoney, FINANCE_DEFAULT_TAB, FINANCE_LENS_TAB, FINANCE_HELD_TAB,
} from './boardApi';
import ResidentDossier from './ResidentDossier';
import { APPROVAL_WORD_LABEL, approvalWordOf } from '../umed/umedApi';

const ID_BASE = 'fn-board';
const EMERGE_GROUP = 'eMerge Finance handoff';
const MONDAY_GROUP = 'Monday (legacy reference, read-only)';
/** The Monday column group's heading: the ruling's label, plus the as-of label (historical mirror, as of <date>) when the server says (2026-10-20). */
const mondayGroupOf = (page: FinanceBoardPage | null): string => (page?.monday_as_of?.label ? `${MONDAY_GROUP} · ${page.monday_as_of.label}` : MONDAY_GROUP);
const LENS_LABEL = 'Legacy Monday rows not yet reconciled';

const cents = (n: number | null | undefined) => (n === null || n === undefined ? null : boardMoney(n / 100));

const PAYMENT_WORDS: Record<string, string> = { NONE: 'no payment recorded', PARTIAL: 'partly paid', PAID_IN_FULL: 'paid in full', WRITTEN_OFF: 'written off' };
const AMOUNT_SOURCE_WORDS: Record<string, string> = { MONDAY_CLOSED_LEAD_TOTAL: 'sale snapshot from the PA close', NATIVE_PRICING_CATALOG: 'price catalog' };
const PRIVATE_PAY_LABEL = 'Private pay (no UMED)';
const isPrivatePay = (r: FinanceBoardRow) => r.handoff?.kind === 'PRIVATE_PAY';

const HELD_TAG = 'Approved in eMerge · no Finance handoff';
const HELD_LABEL = 'Approved, no Finance handoff';
const LINK_WORDS: Record<string, string> = {
  THIS_RESIDENT: 'this resident\'s Monday row',
  OTHER_RESIDENT: 'another resident\'s Monday row',
  NO_RESIDENT: 'a Monday row that names no resident',
  MISSING: 'no Monday row eMerge holds',
  NONE: 'no Monday link',
};
const HELD_WHY = 'Approved in eMerge, and no Finance handoff exists for it. Most such cases are linked to a Monday Finance row: while that rule stands, eMerge opens no Finance handoff for them. The rule awaits Isaiah\'s ruling.';

/** Processes 5 and 6: a UMED flag Finance is told about. A chip, not an action; no amount changed. */
function FlagChip({ f }: { f: FinanceBoardRow['flags'] }) {
  if (!f || !f.count) return null;
  return (
    <span className="board-tag board-tag--caution board-flag" title={`${f.latest.summary} No amount was changed.${f.count > 1 ? ` ${f.count} UMED flags on this resident.` : ''}`}>
      {f.latest.label} {boardDate(f.latest.on)}{f.count > 1 ? ` (${f.count})` : ''}
    </span>
  );
}

function StateCell({ r }: { r: FinanceBoardRow }) {
  if (!r.emerge_state) {
    return (
      <>
        <NotRecorded label="not reconciled in eMerge yet" why="eMerge holds no handoff to Finance for this resident (UMED or private pay). The row leaves this lens once eMerge holds one." />
        {r.approved_without_handoff && <span className="board-tag board-tag--caution" title={HELD_WHY}>{HELD_TAG}</span>}
        <FlagChip f={r.flags} />
      </>
    );
  }
  const s = r.emerge_state;
  // Ruling #1 (2026-10-07): a correction, reversal or later approval on the episode is an amendment ON this handoff.
  const c = r.handoff?.corrected;
  return (
    <>
      <StateMark word={s.word} color={s.color} marker={s.marker} stateKey={s.key} />
      {c && c.count > 0 && (
        <span className="board-tag board-tag--caution" title={`${c.count} amendment${c.count === 1 ? '' : 's'} recorded on this handoff (never a second handoff)`}>
          {correctedWord(c)}{c.last_on ? ` ${boardDate(c.last_on)}` : ''}
        </span>
      )}
      {/* PAR 01 (2026-10-08): APPROVED - SFS CORRECTION -- collect the approved months; these wait on the correction. */}
      {r.handoff?.awaiting_correction && (
        <span className="board-tag board-tag--caution" title={AWAITING_CORRECTION_WHY}>{awaitingCorrectionLine(r.handoff.awaiting_correction)}</span>
      )}
      {/* CORRECTIONS ruling 2 (2026-10-28): per month, the collectible amount or "Correction", and the total collectible now. */}
      {r.handoff?.awaiting_correction && scheduleLine(r.handoff.awaiting_correction.schedule) && (
        <span className="board-tag" data-testid="board-correction-months" title="Per month: the amount collectible now, or Correction (not collectible until the correction is done).">
          {scheduleLine(r.handoff.awaiting_correction.schedule)}
        </span>
      )}
      <FlagChip f={r.flags} />
    </>
  );
}

function MondayMoneyCell({ m }: { m: MondayMoney | undefined }) {
  const text = m ? boardMoney(m.value) : null;
  if (text === null) return <NotRecorded why="Monday holds no amount here." />;
  return <><span className="board-money">{text}</span><SourceTag source="MONDAY" detail="read-only" /></>;
}

const mondayDated = (d: { on: string | null } | null | undefined) => (d && d.on ? <>{boardDate(d.on)}<SourceTag source="MONDAY" /></> : <NotRecorded />);

function approvalColumns(): BoardColumn<FinanceBoardRow>[] {
  return [
    {
      key: 'approval', label: 'Approval', source: 'EMERGE', group: EMERGE_GROUP,
      // Approved is the act (final verification 2026-10-06, P2-C): a UMED operator moved the case to Approved in eMerge.
      help: 'From the handoff\'s UMED case in eMerge: "Approved" only when a UMED operator moved it to Approved in eMerge. FLMMIS reflected is the evidence that act needs, not the approval; Monday\'s approved stage is Monday\'s claim. A private-pay handoff has no UMED.',
      render: (r) => {
        if (isPrivatePay(r)) return <NotRecorded label="no UMED (private pay)" why="A private-pay sale goes straight to Finance without a UMED, so there is no approval to show." />;
        if (!r.approval) return <NotRecorded label="no UMED case" />;
        const word = approvalWordOf(r.approval);
        return (
          <>
            <span className={word === 'APPROVED' ? 'board-tag board-tag--ok' : word === 'APPROVED_AT_SOURCE' ? 'board-tag board-tag--claim' : 'board-tag'}>
              {word === 'APPROVED' ? 'Approved' : APPROVAL_WORD_LABEL[word]}
            </span>
            {word === 'APPROVED' && r.approval.approved_on ? <span className="board-src">{boardDate(r.approval.approved_on)}</span> : null}
            <SourceTag source="EMERGE" detail="UMED case" />
          </>
        );
      },
    },
    { key: 'noca', label: 'NOCA', source: 'EMERGE', group: EMERGE_GROUP, help: 'NOCA state on the resident\'s UMED case in eMerge.', render: (r) => (r.approval?.noca ? <>{r.approval.noca.toLowerCase().replace(/_/g, ' ')}<SourceTag source="EMERGE" /></> : <NotRecorded />) },
    { key: 'flmmis', label: 'FLMMIS', source: 'EMERGE', group: EMERGE_GROUP, help: 'FLMMIS state on the resident\'s UMED case in eMerge.', render: (r) => (r.approval?.flmmis ? <>{r.approval.flmmis.toLowerCase().replace(/_/g, ' ')}<SourceTag source="EMERGE" /></> : <NotRecorded />) },
  ];
}

const notesColumn = (group: string): BoardColumn<FinanceBoardRow> => ({
  key: 'notes', label: 'Notes', numeric: true, source: 'EMERGE', group,
  help: 'Finance notes recorded in eMerge for this resident.',
  render: (r) => (r.notes_count === null ? <NotRecorded label="no resident" /> : <>{r.notes_count}<SourceTag source="EMERGE" /></>),
});

const facilityColumn = (group: string): BoardColumn<FinanceBoardRow> => ({
  key: 'facility', label: 'Building', sort: 'facility', source: 'EMERGE', group,
  help: 'The resident\'s current confirmed building in eMerge (the same building that decides who may see the row).',
  render: (r) => (r.facility ? <><span className="board-clip" title={r.facility.name || undefined}>{r.facility.name || 'Name not recorded'}</span><SourceTag source="EMERGE" detail="current building" /></> : <NotRecorded label="no current building" />),
});

/** ATD (approved to dispense) is eMerge's own word, recorded in the resident's ATD panel. Blank is "not recorded", never "No". */
const atdColumn = (group: string): BoardColumn<FinanceBoardRow> => ({
  key: 'atd', label: 'ATD', source: 'EMERGE', group,
  help: 'Approved to dispense, the word eMerge holds for the resident (Yes, Dispensed, No, NO-DECEASED, NO-MOVED, REVERSAL, REFUSED). Blank means no ATD is recorded; it is not a No.',
  render: (r) => {
    const a = r.atd;
    if (!a || !a.status) return <NotRecorded label="not recorded" />;
    const yes = a.status === 'Yes';
    return (
      <>
        <span className={yes || a.status === 'Dispensed' ? 'board-tag board-tag--ok' : 'board-tag'}>
          {yes ? `ATD · ${a.atd_date ? `since ${boardDate(a.atd_date)}` : 'date not recorded'}` : a.status}
        </span>
        <SourceTag source="EMERGE" detail="ATD" />
      </>
    );
  },
});

/** Main table and the state tabs: eMerge columns only. Monday's row is not a column here; it is provenance in the dossier. */
function handoffColumns(page: FinanceBoardPage | null): BoardColumn<FinanceBoardRow>[] {
  const cols: BoardColumn<FinanceBoardRow>[] = [
    {
      key: 'state', label: 'eMerge state', sort: 'state', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The handoff\'s one eMerge state, as a word and a shape. From the Finance handoff record, or "handed by UMED, not received" while Finance holds none yet. Never derived from dates. "Corrected <date>": a reversal, a later approval or a partial approval\'s correction of the same UMED episode, recorded on this handoff (never a second one). "SFS correction completed <date>": a correction on the case was completed.',
      render: (r) => <StateCell r={r} />,
    },
    facilityColumn(EMERGE_GROUP),
    atdColumn(EMERGE_GROUP),
    {
      key: 'handed', label: 'Handed to Finance', sort: 'handed', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'When the handoff reached Finance, and from where: UMED (full approval, correction completed, reversal completed), or a Patient Advocate\'s private-pay sale (no UMED).',
      render: (r) => {
        if (!r.handoff?.handed_at) return <NotRecorded />;
        if (isPrivatePay(r)) {
          const by = r.handoff.handed_by?.name;
          return (
            <span className="board-dated">
              <span className="board-tag board-tag--pp">{r.handoff.label || PRIVATE_PAY_LABEL}</span>
              <span className="board-dated__value">{boardDate(r.handoff.handed_at)}</span>
              <SourceTag source="EMERGE" detail={by ? `handed by ${by}` : 'Patient Advocate'} />
            </span>
          );
        }
        return <span className="board-dated"><span className="board-dated__value">{boardDate(r.handoff.handed_at)}</span><SourceTag source="EMERGE" detail={r.handoff.opened_from ? `UMED · ${r.handoff.opened_from.words}` : 'UMED'} /></span>;
      },
    },
    {
      key: 'attention', label: 'Attention', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'Facts Finance should see before acting: UMED cancelled its obligation while Finance holds the handoff; a death reported in eMerge.',
      render: (r) => {
        const flags: React.ReactNode[] = [];
        if (r.handoff?.attention === 'UMED_OBLIGATION_CANCELLED') flags.push(<span key="c" className="board-tag board-tag--danger">UMED cancelled its obligation</span>);
        if (r.deceased_reported) flags.push(<span key="d" className="board-tag">deceased reported{r.deceased_reported.effective_date ? ` ${boardDate(r.deceased_reported.effective_date)}` : ''}</span>);
        return flags.length ? <>{flags}<SourceTag source="EMERGE" /></> : <span className="board-na">none</span>;
      },
    },
    {
      key: 'acceptor', label: 'Accepted by', sort: 'acceptor', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The eMerge user who accepted the handoff, and when. This is the board\'s owner filter; Monday\'s Finance Rep is not used.',
      render: (r) => (r.handoff?.accepted
        ? <span className="board-dated"><span>{r.handoff.accepted.by?.name || 'Name not recorded'}</span><SourceTag source="EMERGE" detail={boardDate(r.handoff.accepted.at) || undefined} /></span>
        : <NotRecorded label="not accepted yet" />),
    },
    {
      key: 'invoice', label: 'Invoice', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The invoice Finance recorded on the handoff in eMerge: its reference and date.',
      render: (r) => (r.handoff?.invoice ? <><span className="board-clip" title={r.handoff.invoice.reference}>{r.handoff.invoice.reference}</span><SourceTag source="EMERGE" detail={boardDate(r.handoff.invoice.created_on) || undefined} /></> : <NotRecorded />),
    },
    {
      key: 'terms', label: 'Terms', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The terms Finance recorded on the handoff in eMerge: months x monthly amount = total. On a private-pay handoff, the terms as sold are shown until Finance records its own; they are what was sold, never Finance\'s terms.',
      render: (r) => {
        const t = r.handoff?.terms;
        const sold = r.handoff?.sold;
        // Who pays rides with a private-pay sale wherever Finance reads it (Isaiah, 2026-10-29); a UMED row has no payer.
        const payer = isPrivatePay(r) ? <span className="board-src" data-testid="pp-payer">{payerLine(sold?.payer)}</span> : null;
        if (!t) {
          // A handoff made under pp-terms-v1-2026-10-07 may be monthly: it still reads N x monthly = total. One month is paid in full.
          return sold
            ? <><NotRecorded /><span className="board-src">as sold: {sold.term_months > 1 ? `${sold.term_months} x ${cents(sold.monthly_cents)} = ${cents(sold.total_cents)}` : `${cents(sold.total_cents)} paid in full`}</span>{payer}</>
            : <><NotRecorded />{payer}</>;
        }
        return <><span className="board-money">{t.term_months} x {cents(t.monthly_cents) || 'amount not recorded'} = {cents(t.total_cents) || 'total not recorded'}</span><SourceTag source="EMERGE" />{payer}</>;
      },
    },
    {
      key: 'payment', label: 'Payment', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The payment state Finance recorded on the handoff in eMerge. It records what Finance saw, not an amount.',
      render: (r) => (r.handoff?.payment_state ? <>{PAYMENT_WORDS[r.handoff.payment_state] || r.handoff.payment_state.toLowerCase()}<SourceTag source="EMERGE" /></> : <NotRecorded label="not received yet" />),
    },
    {
      key: 'liability', label: 'Current liability', numeric: true, source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The resident\'s current monthly liability fact in eMerge, with its verification.',
      render: (r) => (r.liability && r.liability.cents !== null
        ? <><span className="board-money">{cents(r.liability.cents)}</span><SourceTag source="EMERGE" detail={(r.liability.status || 'unverified').toLowerCase()} /></>
        : <NotRecorded />),
    },
    {
      key: 'sale', label: 'Sale amount', numeric: true, source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The sale amount in eMerge, with where that amount came from: the PA case\'s first sale, or the sale a private-pay handoff collects.',
      render: (r) => (r.sale && r.sale.amount !== null
        ? <><span className="board-money">{boardMoney(r.sale.amount)}</span><SourceTag source="EMERGE" detail={r.sale.amount_source ? AMOUNT_SOURCE_WORDS[r.sale.amount_source] || r.sale.amount_source.toLowerCase().replace(/_/g, ' ') : undefined} /></>
        : <NotRecorded />),
    },
  ];
  if (page?.approval_detail) cols.push(...approvalColumns());
  cols.push(notesColumn(EMERGE_GROUP));
  return cols;
}

/** Approved, no Finance handoff: the approved case, the act's date, and where its Monday link points. Outside Main table. */
function heldColumns(page: FinanceBoardPage | null): BoardColumn<FinanceBoardRow>[] {
  const cols: BoardColumn<FinanceBoardRow>[] = [
    {
      key: 'state', label: 'eMerge state', source: 'EMERGE', group: EMERGE_GROUP,
      help: HELD_WHY,
      render: (r) => <span className="board-tag board-tag--caution" title={HELD_WHY}>{HELD_LABEL}</span>,
    },
    facilityColumn(EMERGE_GROUP),
    {
      key: 'approved', label: 'Approved in eMerge', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The day a UMED operator approved the case in eMerge (the approval act\'s own date).',
      render: (r) => (r.held?.approved_on ? <>{boardDate(r.held.approved_on)}<SourceTag source="EMERGE" detail="approval act" /></> : <NotRecorded />),
    },
    {
      key: 'link', label: 'Linked Monday Finance row', source: 'MONDAY', group: EMERGE_GROUP,
      help: 'Where the case\'s Monday Finance link points. Said, never a condition: every held approval is listed, whatever the link names.',
      render: (r) => (r.held ? <>{LINK_WORDS[r.held.monday_link.points_to] || r.held.monday_link.points_to}<SourceTag source="MONDAY" detail="link on the UMED case" /></> : <NotRecorded />),
    },
  ];
  if (page?.approval_detail) cols.push(...approvalColumns());
  cols.push(notesColumn(EMERGE_GROUP));
  return cols;
}

/** The lens: Monday's row, read-only, under the ruling's label; eMerge says only that it holds no handoff. */
function lensColumns(page: FinanceBoardPage | null): BoardColumn<FinanceBoardRow>[] {
  const MG = mondayGroupOf(page);
  const cols: BoardColumn<FinanceBoardRow>[] = [
    { key: 'state', label: 'eMerge state', source: 'EMERGE', group: EMERGE_GROUP, help: 'eMerge holds no handoff to Finance for this resident yet (UMED or private pay), so the row has no eMerge state.', render: (r) => <StateCell r={r} /> },
    facilityColumn(EMERGE_GROUP),
  ];
  if (page?.approval_detail) cols.push(...approvalColumns());
  cols.push(notesColumn(EMERGE_GROUP));
  cols.push(
    {
      key: 'm-status', label: 'Status word', source: 'MONDAY', group: MG,
      help: 'Monday\'s Finance Status word in Monday\'s colour (a bar beside the word). Legacy reference; it decides nothing here.',
      render: (r) => <><StatusBar word={r.monday?.status?.word} color={r.monday?.status?.color ?? null} /><SourceTag source="MONDAY" detail={r.monday && r.monday.items > 1 ? `${r.monday.items} rows for this resident` : undefined} /></>,
    },
    { key: 'm-sale', label: 'Sale value', numeric: true, source: 'MONDAY', group: MG, help: 'Monday\'s Total Sale Value. Legacy reference, read-only; it never sets eMerge Finance state.', render: (r) => <MondayMoneyCell m={r.monday?.sale_value} /> },
    { key: 'm-payments', label: 'Payments received', numeric: true, source: 'MONDAY', group: MG, help: 'Monday\'s Payments Received. Legacy reference, read-only. $0.00 is Monday saying zero; "not recorded" is Monday saying nothing.', render: (r) => <MondayMoneyCell m={r.monday?.payments_received} /> },
    { key: 'm-remaining', label: 'Remaining', numeric: true, source: 'MONDAY', group: MG, help: 'Monday\'s Remaining Amount. Legacy reference, read-only.', render: (r) => <MondayMoneyCell m={r.monday?.remaining} /> },
    { key: 'm-overdue', label: 'Amount overdue', numeric: true, source: 'MONDAY', group: MG, help: 'Monday\'s Amount Overdue, as Monday wrote it. Legacy reference, read-only; eMerge derives nothing from it.', render: (r) => <MondayMoneyCell m={r.monday?.overdue} /> },
    {
      key: 'm-terms', label: 'Terms text', source: 'MONDAY', group: MG,
      help: 'Monday\'s Terms text, quoted as Monday wrote it and never parsed into numbers.',
      render: (r) => (r.monday?.terms ? <><span className="board-clip" title={r.monday.terms.text}>{r.monday.terms.text}</span><SourceTag source="MONDAY" detail="read-only" /></> : <NotRecorded />),
    },
    {
      key: 'm-refund', label: 'Refund', source: 'MONDAY', group: MG, help: 'Monday\'s Refund Amount and Refund Date.',
      render: (r) => (r.monday?.refund ? <>{boardMoney(r.monday.refund.amount) || <NotRecorded />}{r.monday.refund.on ? <span className="board-src">{boardDate(r.monday.refund.on)}</span> : null}<SourceTag source="MONDAY" /></> : <NotRecorded />),
    },
    { key: 'm-reversal', label: 'Reversal date', source: 'MONDAY', group: MG, help: 'Monday\'s Reversal Date.', render: (r) => mondayDated(r.monday?.reversal_date) },
    { key: 'm-dcf', label: 'DCF correction', source: 'MONDAY', group: MG, help: 'Monday\'s Date of DCF Correction.', render: (r) => mondayDated(r.monday?.dcf_correction_date) },
    { key: 'm-deceased', label: 'Deceased date', source: 'MONDAY', group: MG, help: 'Monday\'s Deceased Date.', render: (r) => mondayDated(r.monday?.deceased_date) },
    { key: 'm-invoice', label: 'Invoice created', source: 'MONDAY', group: MG, help: 'Monday\'s INVOICE CREATED.', render: (r) => (r.monday?.invoice_created ? <>{r.monday.invoice_created.value ? 'Yes' : 'No'}<SourceTag source="MONDAY" /></> : <NotRecorded />) },
    {
      key: 'm-facility', label: 'Monday building', source: 'MONDAY', group: MG,
      help: 'The building Monday\'s row links to. Reference only; scope and the Building filter use the resident\'s current building in eMerge.',
      render: (r) => (r.monday?.facility ? <><span className="board-clip" title={r.monday.facility.name || undefined}>{r.monday.facility.name || 'Name not recorded'}</span><SourceTag source="MONDAY" /></> : <NotRecorded />),
    },
  );
  return cols;
}

function FilterSelect({ label, value, options, onChange, allLabel }: { label: string; value: string; options: { id: string; name: string; count?: number }[]; onChange: (v: string) => void; allLabel: string }) {
  return (
    <label className="board-field">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        <option value="">{allLabel}</option>
        {value && !options.some((o) => o.id === value) && <option value={value}>Selected (not in this scope)</option>}
        {options.map((o) => <option key={o.id} value={o.id}>{o.count === undefined ? o.name : `${o.name} (${o.count.toLocaleString('en-US')})`}</option>)}
      </select>
    </label>
  );
}

/** What the tabs are, and the proof that the eleven states make Main table. */
function StatesBasis({ page }: { page: FinanceBoardPage }) {
  const states = page.tabs.filter((t) => t.bucket);
  const main = page.tabs.find((t) => t.key === 'all');
  const sum = states.reduce((a, t) => a + (t.count || 0), 0);
  const fmt = (n: number | undefined) => (n || 0).toLocaleString('en-US');
  return (
    <>
      <p className="board-basis">
        Tabs are eMerge Finance handoff states, not Monday. Every handoff is in exactly one:{' '}
        {states.map((t, i) => <React.Fragment key={t.key}>{i ? ' + ' : ''}{t.label} {fmt(t.count)}</React.Fragment>)} = Main table {fmt(main?.count)}.
        {' '}Monday rows not reconciled yet are kept apart in a temporary lens, outside Main table
        {(page.held?.count || 0) > 0 ? <>; approvals with no Finance handoff are listed apart too ({fmt(page.held?.count)}), and are not handoffs</> : null}.
      </p>
      {main && sum !== main.count && <p className="board-caveat" role="note">The state counts do not add up to Main table ({fmt(sum)} vs {fmt(main.count)}). Tell engineering; do not rely on these counts.</p>}
    </>
  );
}

interface LoadError { message: string; status?: number; code?: string }

/** `onAtdAll`: open "ATD — all residents" (every current resident, finance account or not; 2026-10-13). */
/** `layoutSwitch`: an optional Kanban | Table switch in the controls. FinanceShell passes none since 2026-10-14: the workspace sidebar chooses the layout (the Kanban is the default). */
export default function FinanceBoard({ scope, onAtdAll, layoutSwitch }: { scope: FinanceScope; onAtdAll?: () => void; layoutSwitch?: React.ReactNode }) {
  const [st, update] = useBoardUrlState(['acceptor', 'building', 'atd'], FINANCE_DEFAULT_TAB);
  const [data, setData] = useState<FinanceBoardPage | null>(null);
  const [lastTabs, setLastTabs] = useState<BoardTabDef[]>([]);
  const [lastOptions, setLastOptions] = useState<FinanceBoardPage['filter_options'] | null>(null);
  const [err, setErr] = useState<LoadError | null>(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(st.q);
  const seq = useRef(0);
  useEffect(() => { setDraft(st.q); }, [st.q]);
  // A link that names a tab lands on that tab, always. Only an unnamed landing may move.
  const [sp] = useSearchParams();
  const landing = useRef(!sp.has('tab'));
  const [landedOnLens, setLandedOnLens] = useState(false);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true); setErr(null);
    try {
      const r = await financeBoardApi.board({ tab: st.tab, q: st.q, acceptor: st.filters.acceptor, building: st.filters.building, atd: st.filters.atd, sort: st.sort, dir: st.dir, page: st.page });
      if (mine !== seq.current) return;
      if (r.success && r.data) {
        setData(r.data); setLastTabs(r.data.tabs); setLastOptions(r.data.filter_options);
      } else {
        // Never an empty board, never $0, on a failure: the rows are dropped and the failure is said.
        setData(null);
        setErr({ message: r.code === 'TAB_UNKNOWN' ? 'That view does not exist on this board. The tabs are eMerge Finance handoff states now; Monday status and group views were retired.' : outcomeOf(r), status: r.status, code: r.code });
      }
    } catch {
      if (mine === seq.current) { setData(null); setErr({ message: 'Could not reach the server, so nothing here is current.' }); }
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [st.tab, st.q, st.filters.acceptor, st.filters.building, st.filters.atd, st.sort, st.dir, st.page]);
  useEffect(() => { load(); }, [load]);

  /*
   * While eMerge holds no Finance handoff in scope, Main table is empty and
   * every row in scope is a legacy Monday row not reconciled yet. Landing with
   * no tab named, the board moves once to that lens and says why, so nobody
   * opens to an empty page. It moves once, never on a named link, never with
   * a search or filter, and never back (the Scheduling board's landing).
   */
  useEffect(() => {
    if (!landing.current || !data || data.tab !== FINANCE_DEFAULT_TAB) return;
    landing.current = false;
    if (st.q || st.filters.acceptor || st.filters.building || st.filters.atd) return;
    const lens = data.tabs.find((t) => t.key === FINANCE_LENS_TAB);
    const main = data.tabs.find((t) => t.key === 'all');
    if (main && (main.count || 0) === 0 && lens && (lens.count || 0) > 0) {
      setLandedOnLens(true);
      update({ tab: FINANCE_LENS_TAB, sort: null });
    }
  }, [data, st.q, st.filters.acceptor, st.filters.building, st.filters.atd, update]);
  const lensCount = (data ? data.tabs : lastTabs).find((t) => t.key === FINANCE_LENS_TAB)?.count || 0;
  const mainCount = data ? data.tabs.find((t) => t.key === 'all')?.count : undefined;
  const heldCount = (data && data.held && data.held.count) || 0;
  const heldLinked = (data && data.held && data.held.linked) || 0;

  const onLens = st.tab === FINANCE_LENS_TAB;
  const onHeld = st.tab === FINANCE_HELD_TAB;
  const columns = useMemo(() => (onLens ? lensColumns(data) : onHeld ? heldColumns(data) : handoffColumns(data)), [data, onLens, onHeld]);
  const tabs = data ? data.tabs : lastTabs.map((t) => ({ ...t, count: undefined }));
  const activeTab = tabs.find((t) => t.key === st.tab) || null;
  const options = data ? data.filter_options : lastOptions;
  const filtered = Boolean(st.q || st.filters.acceptor || st.filters.building || st.filters.atd);
  const clear = () => { setDraft(''); update({ q: '', filters: { acceptor: '', building: '', atd: '' } }); };
  const noun = (n: number) => (onLens ? (n === 1 ? 'Monday row' : 'Monday rows') : onHeld ? (n === 1 ? 'approval' : 'approvals') : (n === 1 ? 'handoff' : 'handoffs'));

  const identity = {
    label: 'Resident', sort: 'resident',
    help: 'The resident the handoff (or the legacy Monday row) belongs to. Open a row for their Finance dossier, docked beside the table: balance, who pays, approval, next visit, documents, history, notes, expedited dispense and send-back.',
    render: (r: FinanceBoardRow) => (
      <>
        <strong>{r.resident ? r.resident.name : 'No resident linked'}</strong>
        {r.kind === 'MONDAY_ONLY' && <span className="board-src">legacy Monday row</span>}
      </>
    ),
    name: (r: FinanceBoardRow) => (r.resident ? r.resident.name : 'this row'),
    openable: (r: FinanceBoardRow) => Boolean(r.resident),
    closedWhy: () => 'The Monday row names no resident, so there is no dossier to open.',
  };

  return (
    <section className="board board-frame" aria-label="Finance board">
      {tabs.length > 0 && <BoardTabs tabs={tabs} value={st.tab} onChange={(k) => update({ tab: k, sort: null })} label="Finance board views" idBase={ID_BASE} />}
      <div role="tabpanel" id={panelId(ID_BASE)} aria-labelledby={activeTab ? tabId(ID_BASE, activeTab.key) : undefined} className="board-panel">
        <div className="board-head">
          {activeTab && (
            <p className="board-what">
              {(data && data.what) || activeTab.what}
              <span className="board-what__src">{tabSourceText(activeTab)}</span>
            </p>
          )}
          {data && <StatesBasis page={data} />}
          {landedOnLens && onLens && mainCount === 0 && (
            <p className="board-caveat" role="note">
              Opened here because eMerge holds no Finance handoff in your scope yet, so Main table is empty.
              Every row below is a legacy Monday row not reconciled yet. A row leaves this lens, and its resident joins Main table, once eMerge holds a Finance handoff for the resident.
            </p>
          )}
          {data && heldCount > 0 && (onLens || onHeld || mainCount === 0) && (
            <p className="board-caveat" role="note" data-held={heldCount}>
              {heldCount.toLocaleString('en-US')} {heldCount === 1 ? 'approval' : 'approvals'} in eMerge {heldCount === 1 ? 'has' : 'have'} no Finance handoff
              {heldLinked > 0 ? <>; {heldLinked === heldCount ? (heldCount === 1 ? 'it is' : 'all are') : `${heldLinked.toLocaleString('en-US')} of them are`} linked to a Monday Finance row, and while that rule stands eMerge opens no handoff for {heldLinked === 1 ? 'it' : 'them'} (the rule awaits Isaiah&rsquo;s ruling)</> : null}.
              {' '}{onHeld ? 'They are listed here, outside Main table.' : <>They are listed under &ldquo;{HELD_LABEL}&rdquo;, outside Main table.</>}
              {!onHeld && <>{' '}<button type="button" className="board-btn" onClick={() => update({ tab: FINANCE_HELD_TAB, sort: null })}>Show them</button></>}
            </p>
          )}
          <div className="board-controls">
            <form className="board-search" role="search" onSubmit={(e) => { e.preventDefault(); update({ q: draft.trim() }); }}>
              <label className="board-field"><span>Resident or building</span>
                <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Name or building" aria-label="Search the Finance board" />
              </label>
              <button type="submit" className="board-btn">Find</button>
            </form>
            <FilterSelect label="Accepted by" value={st.filters.acceptor} options={options?.acceptors || []} allLabel="Anyone" onChange={(v) => update({ filters: { acceptor: v } })} />
            <FilterSelect label="Building" value={st.filters.building} options={options?.buildings || []} allLabel="Every building" onChange={(v) => update({ filters: { building: v } })} />
            {options?.atd && <FilterSelect label="ATD" value={st.filters.atd} options={options.atd} allLabel="Any ATD" onChange={(v) => update({ filters: { atd: v } })} />}
            {/* The board lists Finance handoffs only; ATD is for every current resident (Dominique and Josie, 2026-10-13). */}
            {onAtdAll && <button type="button" className="board-btn" onClick={onAtdAll} title="This board lists Finance handoffs only. Open every current resident, with or without a Finance account, to mark ATD.">Mark ATD for any resident</button>}
            {filtered && <button type="button" className="board-btn" onClick={clear}>Clear search and filters</button>}
            {layoutSwitch && <span className="board-layout-switch">{layoutSwitch}</span>}
            {data && (
              <span className="board-result" aria-live="polite">
                <strong>{data.total.toLocaleString('en-US')}</strong> {noun(data.total)} in {activeTab?.label || 'this view'}{filtered ? ' matching your search and filters' : ''}{loading ? ' · updating…' : ''}
              </span>
            )}
          </div>
          {data && (
            <BoardLegend
              title="eMerge handoff states"
              tabs={data.tabs.filter((t) => t.bucket || t.key === 'all')}
              explain
              units={['handoff', 'handoffs']}
              value={st.tab}
              onSelect={(k) => update({ tab: k, sort: null })}
              scope="Each handoff is in exactly one state, shown by a word, a shape and a colour; the eleven counts add up to Main table and follow your search and filters. Nothing here is derived from dates or from Monday."
            />
          )}
        </div>

        {!data && loading && !err && <div className="board-state"><StateBlock kind="loading" shape="table" label="Loading the Finance board…" /></div>}
        {err && (
          <div className="board-state">
            <StateBlock kind="error" status={err.status} message={err.message} onRetry={load} />
            {err.code === 'TAB_UNKNOWN' && <p style={{ marginTop: 8 }}><button type="button" className="board-btn" onClick={() => update({ tab: 'all', sort: null })}>Open the main table</button></p>}
          </div>
        )}
        {data && data.items.length === 0 && data.total > 0 && (
          <div className="board-state">
            <StateBlock kind="empty" reason="filtered" message={`Page ${st.page} is past the end: this view has ${data.total.toLocaleString('en-US')} ${noun(data.total)}.`} onClear={() => update({ page: 1 })} clearLabel="Go to page 1" />
          </div>
        )}
        {data && data.items.length === 0 && data.total === 0 && (
          <div className="board-state">
            {filtered
              ? <StateBlock kind="empty" reason="filtered" message={`No rows in ${activeTab?.label || 'this view'} match your search and filters.`} onClear={clear} clearLabel="Clear search and filters" />
              : !onLens && !onHeld && mainCount === 0 && lensCount > 0
                ? <StateBlock kind="empty" reason="filtered" message={`eMerge holds no Finance handoff in your scope yet. ${lensCount.toLocaleString('en-US')} legacy Monday ${lensCount === 1 ? 'row is' : 'rows are'} not reconciled yet.`} onClear={() => update({ tab: FINANCE_LENS_TAB, sort: null })} clearLabel={`Show the ${LENS_LABEL.toLowerCase()}`} />
                : <StateBlock kind="empty" reason="none" message={`Nothing is in ${activeTab?.label || 'this view'} right now.`} />}
          </div>
        )}
        {data && data.items.length > 0 && (
          <BoardTable<FinanceBoardRow>
            label={`Finance board — ${activeTab?.label || data.tab}, ${data.total} ${noun(data.total)}`}
            columns={columns}
            rows={data.items}
            rowKey={(r) => r.id}
            identity={identity}
            openKey={st.open}
            onToggle={(k) => update({ open: st.open === k ? null : k })}
            detailLabel={(r) => `${r.resident ? r.resident.name : 'Row'}: Finance dossier`}
            renderDetail={(r) => (r.resident ? (
              <>
                {r.monday && r.monday.items > 1 && <p className="board-basis" style={{ padding: '8px 16px 0' }}>Monday holds {r.monday.items} legacy rows for this resident; the dossier is the resident&rsquo;s, the same for each.</p>}
                {/* Mark ATD in the dossier's ATD panel re-reads the board, so the row's ATD column is current (2026-10-15). */}
                <ResidentDossier patientId={r.resident.id} scope={scope} onAtdChanged={() => { load(); }} />
              </>
            ) : null)}
            sort={data.sort} dir={data.dir} sorts={data.sorts}
            onSort={(col, dir) => update({ sort: col, dir })}
            busy={loading}
          />
        )}
        {data && <Pager page={data.page} limit={data.limit} total={data.total} onPage={(p) => update({ page: p })} label={onLens ? 'Monday rows' : onHeld ? 'approvals' : 'handoffs'} />}
      </div>
      <div className="board-foot">
        {data && activeTab && (
          <p>
            Counted and listed by one predicate ({activeTab.predicate}); the count on the tab is the number of rows here.
            {' '}&ldquo;not recorded&rdquo; means the source holds no value, never $0 and never a failed column.
          </p>
        )}
        <p>
          {MONDAY_GROUP}: the Monday sync last wrote Monday&rsquo;s Finance rows {boardStamp(data?.synced_at) || 'at an unrecorded time'}.
          {' '}They appear only in the &ldquo;{LENS_LABEL}&rdquo; lens, decide no tab, count, order or filter, and never set eMerge Finance state.
        </p>
        {data?.monday_as_of?.warning && <p className="lg-warning" role="note">{data.monday_as_of.warning}</p>}
        <ColumnKey columns={columns} identity={identity} notes={onLens ? [`Columns under "${MONDAY_GROUP}" come from the Monday row itself; a row leaves this lens once eMerge holds the resident's handoff.`] : []} />
      </div>
    </section>
  );
}
